import type { TooltipContent } from "../render/RenderContext.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import { clamp, rectContains, type Point, type Rect } from "../../common/geometry.js";
import { smoothVelocity } from "../core/input.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { GlyphButton } from "./GlyphButton.js";
import { Panel } from "./Panel.js";
import type { Orientation } from "./ScrollBar.js";

export interface CarouselPageOptions {
  tooltip?: string;
  tag?: unknown;
}

/** One slide and the panel its contents live in. */
export interface CarouselPage extends CarouselPageOptions {
  readonly panel: Panel;
}

export interface CarouselOptions extends ContainerOptions {
  /** The axis pages travel along. */
  orientation?: Orientation;
  /** Next from the last page returns to the first, and back again. */
  wrapAround?: boolean;
  showArrows?: boolean;
  showIndicators?: boolean;
  /** Seconds one slide takes. Zero cuts straight to the next page. */
  slideSeconds?: number;
  /** Seconds between automatic advances. Zero, the default, is off. */
  autoAdvanceSeconds?: number;
  /** Drag a page sideways to move between them. On by default. */
  swipe?: boolean;
  selectedIndex?: number;
}

const ARROW = 28;
const INDICATOR_STRIP = 22;
const DOT = 8;
const DOT_GAP = 8;
/** How much of a page a swipe must pull across before it lands on the next. */
const SWIPE_COMMIT = 0.4;
/**
 * Page pixels per second past which letting go lands the next page however
 * short the pull was. What makes a flick of the thumb work.
 */
const FLICK_SPEED = 320;

/** Seconds on a monotonic clock that runs whether or not frames are. */
const now = (): number => performance.now() / 1000;

/**
 * A strip of pages showing one at a time, with arrows, dots and a slide.
 *
 * The same ownership as `TabControl` — each page is a `Panel` the control
 * creates and sizes, so an application fills `page.panel.controls` and never
 * positions anything — but the chooser is a pair of arrows and a row of dots
 * rather than a tab strip, and moving between pages is animated.
 *
 * Only the two pages involved in a slide are ever visible, which is what makes
 * wrap-around simple: the incoming page is placed on whichever side the
 * gesture came from rather than the side its index implies.
 *
 * Pages can also be dragged across by hand — a finger anywhere over a page, or
 * a mouse doing the same. The pull and the slide are one mechanism: letting go
 * part-way starts a slide from where the hand left it, either onwards to the
 * page it chose or back to the one it came from.
 */
export class Carousel extends Container {
  #pages: CarouselPage[] = [];
  #selected = -1;
  /**
   * The slide in flight: where it came from, how far along it is, and how
   * much of it a hand had already done before letting go.
   */
  #slide: { from: number; direction: 1 | -1; startedAt: number; start: number } | null = null;
  /**
   * The pull in flight: how far the pages have been dragged off center, and
   * how fast, so letting go knows whether it was a flick.
   */
  #drag: { offset: number; velocity: number; at: number } | null = null;
  #hoverDot = -1;
  #animating = false;
  #autoTimer = 0;

  orientation: Orientation;
  wrapAround: boolean;
  showArrows: boolean;
  showIndicators: boolean;
  slideSeconds: number;
  /** Whether a page can be dragged across by finger or mouse. */
  swipe: boolean;

  readonly previousButton: GlyphButton;
  readonly nextButton: GlyphButton;

  constructor(options: CarouselOptions = {}) {
    super({ width: 400, height: 260, tabStop: true, ...options });
    this.orientation = options.orientation ?? "horizontal";
    this.wrapAround = options.wrapAround ?? true;
    this.showArrows = options.showArrows ?? true;
    this.showIndicators = options.showIndicators ?? true;
    this.slideSeconds = options.slideSeconds ?? 0.28;
    this.swipe = options.swipe ?? true;
    this.#autoSeconds = options.autoAdvanceSeconds ?? 0;

    const back = this.horizontal ? "chevron-left" : "chevron-up";
    const on = this.horizontal ? "chevron-right" : "chevron-down";
    this.previousButton = new GlyphButton({ glyph: back, tooltip: "Previous", tabStop: false });
    this.nextButton = new GlyphButton({ glyph: on, tooltip: "Next", tabStop: false });
    this.previousButton.on("click", () => this.previous());
    this.nextButton.on("click", () => this.next());
    this.controls.add(this.previousButton);
    this.controls.add(this.nextButton);

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerleave", () => this.#setHoverDot(-1));
    this.on("keydown", (e) => this.#onKeyDown(e));
    if (options.selectedIndex !== undefined) this.#selected = options.selectedIndex;
    this.performLayout();
  }

  get horizontal(): boolean {
    return this.orientation === "horizontal";
  }

  // --- pages --------------------------------------------------------------

  get pages(): readonly CarouselPage[] {
    return this.#pages;
  }

  get count(): number {
    return this.#pages.length;
  }

  /** Add a page and return the panel to fill. */
  addPage(options: CarouselPageOptions = {}): Panel {
    const panel = new Panel({ style: "none", visible: false });
    this.#pages.push({ panel, ...options });
    this.controls.add(panel);
    // The arrows overlap the ends of a sliding page, so they paint last.
    this.controls.moveTo(this.previousButton, this.controls.length - 1);
    this.controls.moveTo(this.nextButton, this.controls.length - 1);
    if (this.#selected < 0) this.#selected = 0;
    this.performLayout();
    this.invalidate();
    return panel;
  }

  removePage(index: number): void {
    const page = this.#pages[index];
    if (!page) return;
    this.#pages.splice(index, 1);
    this.controls.remove(page.panel);
    this.#slide = null;
    this.#drag = null;
    if (this.#pages.length === 0) this.#selected = -1;
    else if (this.#selected >= this.#pages.length) this.#selected = this.#pages.length - 1;
    else if (this.#selected > index) this.#selected -= 1;
    this.performLayout();
    this.invalidate();
    this.emit("selectionchanged");
  }

  pageAt(index: number): CarouselPage | undefined {
    return this.#pages[index];
  }

  get selectedIndex(): number {
    return this.#selected;
  }
  set selectedIndex(v: number) {
    this.showPage(v);
  }

  get selectedPage(): CarouselPage | undefined {
    return this.#pages[this.#selected];
  }

  get selectedPanel(): Panel | null {
    return this.#pages[this.#selected]?.panel ?? null;
  }

  // --- moving between pages -----------------------------------------------

  /**
   * Show `index`, sliding from whichever side `direction` says.
   *
   * The direction is passed rather than derived, because wrapping from the
   * last page to the first should carry on forwards, not rewind through
   * everything in between.
   */
  showPage(index: number, direction?: 1 | -1): void {
    const n = this.#pages.length;
    if (n === 0) return;
    const next = clamp(index, 0, n - 1);
    if (next === this.#selected) return;
    this.#goTo(next, direction ?? (next > this.#selected ? 1 : -1), 0);
  }

  /**
   * Move to `index`, starting the slide `start` of the way through it.
   *
   * `start` is what a swipe has already done by hand: a page let go halfway
   * across has half a slide left, not a whole one from where it is not.
   */
  #goTo(index: number, direction: 1 | -1, start: number): void {
    const from = this.#selected;
    this.#selected = index;
    this.#drag = null;
    if (from >= 0 && this.slideSeconds > 0 && this.desktop) {
      this.#beginSlide(from, direction, start);
    } else {
      this.#slide = null;
    }
    this.#syncAnimation();
    this.performLayout();
    this.#restartAuto();
    this.invalidate();
    this.emit("selectionchanged");
  }

  #beginSlide(from: number, direction: 1 | -1, start: number): void {
    // The wall clock, not `desktop.frameTime`. Both are live now that the
    // frame clock is stamped every tick rather than every paint, but a slide
    // is read from the same `now()` on every frame of it, so keeping the
    // start on that clock too means it never mixes two time bases.
    this.#slide = { from, direction, startedAt: now(), start: clamp(start, 0, 1) };
  }

  /** The page one step away in `direction`, wrapping round the ends. */
  #neighbor(direction: 1 | -1): number {
    const n = this.#pages.length;
    return (this.#selected + direction + n) % n;
  }

  next(): void {
    const n = this.#pages.length;
    if (n === 0) return;
    if (this.#selected >= n - 1) {
      if (this.wrapAround) this.showPage(0, 1);
      return;
    }
    this.showPage(this.#selected + 1, 1);
  }

  previous(): void {
    const n = this.#pages.length;
    if (n === 0) return;
    if (this.#selected <= 0) {
      if (this.wrapAround) this.showPage(n - 1, -1);
      return;
    }
    this.showPage(this.#selected - 1, -1);
  }

  /** True while a slide is still running. */
  get sliding(): boolean {
    return this.#slide !== null && this.slideProgress < 1;
  }

  // --- swiping ------------------------------------------------------------

  /** True while a page is being dragged across by hand. */
  get swiping(): boolean {
    return this.#drag !== null;
  }

  /** How far the pages have been pulled off center, in pixels. */
  get swipeOffset(): number {
    return this.#drag?.offset ?? 0;
  }

  /** A mouse drag over a page swipes it, the same as a finger does. */
  override get pansWithMouse(): boolean {
    return this.swipe && this.#pages.length > 1;
  }

  /**
   * A finger or mouse dragging a page across.
   *
   * Only the axis the pages travel along is taken, and only as far as one
   * page: what is left over chains outward, so a carousel at its last page
   * with nowhere to wrap to hands the pull to whatever it sits in rather than
   * stretching. The cross axis is never taken at all — a vertical scroll
   * through a horizontal carousel belongs to the pane around it.
   */
  override panBy(dx: number, dy: number): Point {
    const used = this.#dragBy(this.horizontal ? dx : dy);
    return this.horizontal ? { x: used, y: 0 } : { x: 0, y: used };
  }

  /**
   * Letting go lands a page, so the release is ours and never a fling.
   *
   * The speed the gesture reports is not read: the pull tracks its own, along
   * the axis and in the page's own pixels, so a flick of a finger, of a mouse
   * and of a page moved by code are all measured the same way.
   */
  override panEnd(_vx?: number, _vy?: number): boolean {
    if (!this.#drag) return false;
    this.#endDrag();
    return true;
  }

  /**
   * Pull the pages along by `along`, in the same sense as `panBy`: positive
   * travels towards the next page, because the pages move against the hand.
   * Returns how much of it was used.
   */
  #dragBy(along: number): number {
    if (!this.swipe || this.inputBlocked || this.#pages.length < 2) return 0;
    const size = this.#pageSize;
    if (size <= 0 || along === 0) return 0;

    const drag = this.#drag;
    // Grabbing a carousel mid-slide stops it on the page it was heading for,
    // which is the page the pull is then measured from.
    const from = drag?.offset ?? 0;
    // A page can be pulled one width and no further, and only towards a page
    // there is one to reach: the end of a carousel that does not wrap is a
    // wall, not a rubber band.
    const back = this.wrapAround || this.#selected > 0 ? size : 0;
    const on = this.wrapAround || this.#selected < this.#pages.length - 1 ? size : 0;
    const offset = clamp(from - along, -on, back);
    const used = from - offset;
    if (used === 0 && !drag) return 0;

    const at = now();
    // The same estimate a throw is measured with, so a flick of a carousel
    // and a flick of a list are the same flick — and a hand that paused
    // before letting go has no speed to land a page with.
    const velocity = drag ? smoothVelocity(drag.velocity, offset - from, at - drag.at) : 0;

    if (!drag) {
      this.#slide = null;
      this.#restartAuto(); // no advance out from under a hand that is dragging
    }
    this.#drag = { offset, velocity, at };
    this.#syncAnimation();
    this.performLayout();
    this.invalidate();
    return used;
  }

  /**
   * Let go: land on the page the pull chose, or fall back to this one.
   *
   * Either way it finishes as a slide, started part-done — the page carries on
   * from where the hand left it instead of jumping to an end it had nearly
   * reached.
   */
  #endDrag(): void {
    const drag = this.#drag;
    if (!drag) return;
    const size = this.#pageSize;
    const progress = size > 0 ? Math.abs(drag.offset) / size : 0;
    this.#drag = null;
    if (progress === 0) {
      this.#restartAuto();
      this.performLayout();
      this.invalidate();
      return;
    }
    // Pulled left, or up: the next page is the one being brought in.
    const direction: 1 | -1 = drag.offset < 0 ? 1 : -1;
    // A flick counts only when it is still traveling the way the page went;
    // a pull one way ending in a jerk back the other is not a throw.
    const flick =
      Math.abs(drag.velocity) >= FLICK_SPEED && Math.sign(drag.velocity) === Math.sign(drag.offset);
    // Checked again rather than trusted from the pull: wrapping can be turned
    // off while a page is being held, and there is then nowhere to land.
    const somewhereToGo =
      this.wrapAround ||
      (direction === 1 ? this.#selected < this.#pages.length - 1 : this.#selected > 0);
    if (somewhereToGo && (progress >= SWIPE_COMMIT || flick)) {
      this.#goTo(this.#neighbor(direction), direction, progress);
      return;
    }
    // Nothing was chosen, so the same slide runs backwards: the page that was
    // pulled in goes back out and this one comes back to the middle.
    const back: 1 | -1 = direction === 1 ? -1 : 1;
    if (this.slideSeconds > 0 && this.desktop) {
      this.#beginSlide(this.#neighbor(direction), back, 1 - progress);
    } else {
      this.#slide = null;
    }
    this.#syncAnimation();
    this.performLayout();
    this.#restartAuto();
    this.invalidate();
  }

  /**
   * How far the current slide has run, 0..1. One when nothing is moving.
   *
   * Of what is left to run: a slide a swipe has already taken most of the way
   * takes proportionally less time, so the page keeps its speed rather than
   * crawling the last few pixels over a full `slideSeconds`.
   */
  get slideProgress(): number {
    const slide = this.#slide;
    if (!slide || this.slideSeconds <= 0) return 1;
    const seconds = this.slideSeconds * (1 - slide.start);
    if (seconds <= 0) return 1;
    return clamp((now() - slide.startedAt) / seconds, 0, 1);
  }

  /**
   * Where the pages are along the slide, 0..1, eased.
   *
   * A slide of its own smooths both ends, so it does not begin at full speed.
   * One a hand began is already at speed, so it only eases out — easing into a
   * page the finger had moving is a stall the eye catches.
   */
  #eased(): number {
    const slide = this.#slide;
    const t = this.slideProgress;
    if (slide && slide.start > 0) {
      return slide.start + (1 - slide.start) * (1 - (1 - t) ** 2);
    }
    return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
  }

  // --- automatic advance --------------------------------------------------

  #autoSeconds: number;

  /** Seconds between automatic advances; zero stops it. */
  get autoAdvanceSeconds(): number {
    return this.#autoSeconds;
  }
  set autoAdvanceSeconds(v: number) {
    this.#autoSeconds = Math.max(0, v);
    this.#restartAuto();
  }

  #restartAuto(): void {
    if (this.#autoTimer !== 0) clearInterval(this.#autoTimer);
    this.#autoTimer = 0;
    if (this.#autoSeconds <= 0) return;
    this.#autoTimer = setInterval(() => {
      // Nothing to advance to, nothing to advance while off screen, and
      // nothing that pulls a page out from under a hand holding it.
      if (this.#pages.length < 2 || !this.effectivelyVisible || this.#drag) return;
      this.next();
    }, this.#autoSeconds * 1000) as unknown as number;
  }

  // --- geometry -----------------------------------------------------------

  /** Strip along the far edge holding the dots, empty when they are off. */
  get indicatorRect(): Rect {
    const r = this.localRect;
    if (!this.showIndicators || this.#pages.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    return this.horizontal
      ? { x: 0, y: r.height - INDICATOR_STRIP, width: r.width, height: INDICATOR_STRIP }
      : { x: r.width - INDICATOR_STRIP, y: 0, width: INDICATOR_STRIP, height: r.height };
  }

  /**
   * Where the pages sit: everything the arrows and dots have not taken.
   *
   * Not the client rect. Children are laid out in the control's own space so
   * the arrows, which live outside this rectangle, are children like anything
   * else — a page mid-slide is kept inside it by the clip in `paintChildren`
   * and by the arrows painting over the ends.
   */
  get contentRect(): Rect {
    const r = this.localRect;
    const strip = this.indicatorRect;
    const arrows = this.showArrows && this.#pages.length > 1 ? ARROW : 0;
    return this.horizontal
      ? {
          x: arrows,
          y: 0,
          width: Math.max(0, r.width - arrows * 2),
          height: Math.max(0, r.height - strip.height),
        }
      : {
          x: 0,
          y: arrows,
          width: Math.max(0, r.width - strip.width),
          height: Math.max(0, r.height - arrows * 2),
        };
  }

  /** Everything but the indicator strip: what a slide may be seen inside. */
  get stageRect(): Rect {
    const r = this.localRect;
    const strip = this.indicatorRect;
    return this.horizontal
      ? { x: 0, y: 0, width: r.width, height: Math.max(0, r.height - strip.height) }
      : { x: 0, y: 0, width: Math.max(0, r.width - strip.width), height: r.height };
  }

  /**
   * A compact square centered across the stage, not a full-height column.
   *
   * The column is what the content inset reserves; the button itself only
   * needs to be big enough to hit.
   */
  #arrowRect(atEnd: boolean): Rect {
    const stage = this.stageRect;
    const size = Math.min(ARROW, this.horizontal ? stage.height : stage.width);
    if (this.horizontal) {
      return {
        x: atEnd ? stage.width - ARROW : 0,
        y: Math.round(stage.y + (stage.height - size) / 2),
        width: ARROW,
        height: size,
      };
    }
    return {
      x: Math.round(stage.x + (stage.width - size) / 2),
      y: atEnd ? stage.height - ARROW : 0,
      width: size,
      height: ARROW,
    };
  }

  get previousRect(): Rect {
    return this.#arrowRect(false);
  }

  get nextRect(): Rect {
    return this.#arrowRect(true);
  }

  /** How far a page travels to replace another: the content area, along. */
  get #pageSize(): number {
    const content = this.contentRect;
    return this.horizontal ? content.width : content.height;
  }

  /** The dot for page `index`, in local coordinates. */
  dotRect(index: number): Rect {
    const strip = this.indicatorRect;
    const n = this.#pages.length;
    if (n === 0 || strip.width === 0 || strip.height === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    const span = n * DOT + (n - 1) * DOT_GAP;
    if (this.horizontal) {
      const start = strip.x + (strip.width - span) / 2;
      return {
        x: Math.round(start + index * (DOT + DOT_GAP)),
        y: Math.round(strip.y + (strip.height - DOT) / 2),
        width: DOT,
        height: DOT,
      };
    }
    const start = strip.y + (strip.height - span) / 2;
    return {
      x: Math.round(strip.x + (strip.width - DOT) / 2),
      y: Math.round(start + index * (DOT + DOT_GAP)),
      width: DOT,
      height: DOT,
    };
  }

  /** Dot index at a local point, or -1. */
  dotAt(p: Point): number {
    if (!this.showIndicators) return -1;
    for (let i = 0; i < this.#pages.length; i++) {
      // A generous target: the dots themselves are 8px.
      const r = this.dotRect(i);
      const hit = { x: r.x - 4, y: r.y - 6, width: r.width + 8, height: r.height + 12 };
      if (rectContains(hit, p)) return i;
    }
    return -1;
  }

  // --- layout -------------------------------------------------------------

  /** The arrows are ours to place; the pages are laid out by the slide. */
  protected override applyAnchors(): void {}

  protected override performLayout(): void {
    if (!this.previousButton) return; // reached from Container's constructor
    const content = this.contentRect;
    const prev = this.previousRect;
    const next = this.nextRect;
    this.previousButton.visible = this.showArrows && this.#pages.length > 1;
    this.nextButton.visible = this.previousButton.visible;
    this.previousButton.setBounds(prev.x, prev.y, prev.width, prev.height);
    this.nextButton.setBounds(next.x, next.y, next.width, next.height);

    const size = this.horizontal ? content.width : content.height;
    const move = this.#travel(size);

    this.#pages.forEach((page, i) => {
      let offset: number | null = null;
      if (i === this.#selected) offset = move ? move.offset : 0;
      else if (move && i === move.partner) offset = move.partnerOffset;
      if (offset === null) {
        page.panel.visible = false;
        return;
      }
      page.panel.visible = true;
      page.panel.setBounds(
        content.x + (this.horizontal ? Math.round(offset) : 0),
        content.y + (this.horizontal ? 0 : Math.round(offset)),
        content.width,
        content.height,
      );
    });

    if (this.#slide && this.#eased() >= 1) this.#slide = null;
    this.#syncAnimation();
  }

  /**
   * Where the pages are right now, or null when one page is simply showing.
   *
   * A pull and a slide are the same picture — the selected page pushed off
   * center and one other page a full width to one side of it — but which page
   * is which differs, and that is the whole of the difference between them. A
   * slide has already moved the selection, so the selected page is the one
   * arriving and its partner is the one leaving. A pull has not, so the
   * selected page is the one leaving and the partner is being brought in on
   * the side the hand came from — which is what lets a pull off the end carry
   * on forwards instead of rewinding through everything in between.
   */
  #travel(size: number): { offset: number; partner: number; partnerOffset: number } | null {
    const drag = this.#drag;
    if (drag) {
      if (drag.offset === 0) return null;
      const direction: 1 | -1 = drag.offset < 0 ? 1 : -1;
      return {
        offset: drag.offset,
        partner: this.#neighbor(direction),
        partnerOffset: drag.offset + direction * size,
      };
    }
    const slide = this.#slide;
    if (!slide) return null;
    const eased = this.#eased();
    if (eased >= 1) return null;
    const offset = slide.direction === 1 ? size * (1 - eased) : -size * (1 - eased);
    return { offset, partner: slide.from, partnerOffset: offset - slide.direction * size };
  }

  protected override onEnvironmentChanged(): void {
    this.performLayout();
  }

  /** A frame every tick, but only while something is actually moving. */
  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.#slide !== null && this.effectivelyVisible;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  // --- input --------------------------------------------------------------

  #setHoverDot(index: number): void {
    if (this.#hoverDot === index) return;
    this.#hoverDot = index;
    this.invalidate();
  }

  #onPointerMove(e: MouseEventArgs): void {
    this.#setHoverDot(this.dotAt({ x: e.x, y: e.y }));
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (this.inputBlocked || e.button !== 0) return;
    const index = this.dotAt({ x: e.x, y: e.y });
    if (index < 0) return;
    e.handled = true;
    this.focus();
    if (index !== this.#selected) this.showPage(index, index > this.#selected ? 1 : -1);
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (this.inputBlocked || !this.focused || e.ctrlKey || e.altKey || e.metaKey) return;
    const back = this.horizontal ? "ArrowLeft" : "ArrowUp";
    const on = this.horizontal ? "ArrowRight" : "ArrowDown";
    switch (e.key) {
      case back:
        this.previous();
        break;
      case on:
        this.next();
        break;
      case "Home":
        this.showPage(0, -1);
        break;
      case "End":
        this.showPage(this.#pages.length - 1, 1);
        break;
      default:
        return;
    }
    e.handled = true;
  }

  override getTooltip(p: Point): TooltipContent | null {
    const index = this.dotAt(p);
    if (index >= 0) return this.#pages[index]?.tooltip ?? `${index + 1} of ${this.#pages.length}`;
    return this.tooltip;
  }

  // --- painting -----------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    // An animator keeps frames coming but runs no layout of its own, and the
    // slide *is* a layout — so advance it here, before the children paint.
    // `performLayout` clears the slide once it has run out, which is what
    // takes the animator back off again.
    if (this.#slide) this.performLayout();
    theme.drawPanel(rc, this.localRect, false);
  }

  protected override paintChildren(rc: RenderContext, theme: Theme): void {
    // A page part-way through a slide must not run over the dots, and the
    // arrows have to survive the clip, so it is the stage rather than the
    // content area. The arrows paint last and cover the ends.
    rc.clipped(this.stageRect, () => super.paintChildren(rc, theme));
  }

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    // Arrows are children and paint themselves; the dots are chrome.
    if (!this.showIndicators) return;
    for (let i = 0; i < this.#pages.length; i++) {
      theme.drawCarouselDot(rc, this.dotRect(i), i === this.#selected, i === this.#hoverDot);
    }
  }
}

